import { format } from 'date-fns'
import { z } from 'zod'

import {
  ExternalTournament,
  ExternalTournamentWithEvents,
  ImportTournamentEventPayload,
  TournamentEvent
} from '@img-arena/consoles-api-streaming'
import { US_COUNTRY_CODE } from '@/constants/countryCodes'

import { DEFAULT_EMPTY_LIST, US_SUBDIVISIONS } from './constants'

const isSubDivision = (territory: string): boolean => territory.substring(0, 3) === 'US-'

const isUS = (territory: string): boolean => territory === US_COUNTRY_CODE

export const isImportedTournamentEvent = (row: TournamentEvent) => {
  return 'exchangeEventId' in row
}

export const isImportedTournament = (tournament: ExternalTournamentWithEvents) => {
  return 'exchangeTournamentId' in tournament && tournament.exchangeTournamentId !== null
}

export const sanitizeGeoBlockingTerritories = (territoriesJSON: string): string => {
  let territories: string[] = []

  try {
    territories = JSON.parse(territoriesJSON)
  } catch (error) {
    console.error(`Failed to parse territories: ${error}`)
  }

  if (!territories || !territories.length) {
    return DEFAULT_EMPTY_LIST
  }

  const subDivisions = territories.filter(isSubDivision)
  const areUSSubdivisions = subDivisions.length && subDivisions.length === US_SUBDIVISIONS.length
  const removeUS = subDivisions.length && !areUSSubdivisions
  if (subDivisions) {
    territories.push(US_COUNTRY_CODE)
  }

  const sanitizedTerritories = territories.filter((territory, index) => {
    const withoutUS = removeUS && isUS(territory)
    const withoutSubDivision = areUSSubdivisions && isSubDivision(territory)
    return (
      territory &&
      territory.trim() &&
      !withoutUS &&
      !withoutSubDivision &&
      territories.indexOf(territory) === index
    )
  })

  return JSON.stringify(sanitizedTerritories)
}

export const formatEventQualifier = (eventId: TournamentEvent['id']): string => {
  if (
    eventId.indexOf('QS') > 0 ||
    eventId.indexOf('QD') > 0 ||
    eventId.indexOf('RS') > 0 ||
    eventId.indexOf('RD') > 0
  ) {
    return 'Q'
  }

  return ''
}

export const formatExternalEvent = (
  event: TournamentEvent,
  exchangeTournamentId: ExternalTournament['exchangeTournamentId']
): ImportTournamentEventPayload & {
  court: TournamentEvent['court']
  matchType: TournamentEvent['matchType']
} => {
  const isUUID = z.string().uuid().safeParse(event.id).success

  return {
    id: 0,
    tournament: { id: exchangeTournamentId },
    title: event.name,
    startDate: format(event.startDate, "yyyy-MM-dd'T'HH:mm:ss'+0000'"),
    endDate: format(event.endDate, "yyyy-MM-dd'T'HH:mm:ss'+0000'"),
    court: event.court,
    gfxConfig: isUUID ? null : { externalEventId: event.id },
    arenaId: isUUID ? event.id : null,
    contacts: '{"contacts":[]}',
    geoListField: DEFAULT_EMPTY_LIST,
    preferedZixiServer: null,
    preferedBackupZixiServer: null,
    matchType: event.ddeMatchType,
    competitors: event.competitors,
    homeTeamId: event.homeTeamId
  }
}

export const prepareEventData = (event: ImportTournamentEventPayload) => {
  const newEvent = { ...event }

  if (event.contacts && typeof event.contacts !== 'string') {
    const contacts = event.contacts.map(({ title, name, number: phoneNumber }) => ({
      title,
      name,
      number: phoneNumber
    }))
    newEvent.contacts = JSON.stringify(contacts)
  }

  let geoListField = newEvent.geoListField

  if (typeof event.geoListField !== 'string') {
    geoListField = JSON.stringify(event.geoListField || [])
  }

  newEvent.geoListField = sanitizeGeoBlockingTerritories(geoListField)

  return newEvent
}
